'use client';

import { maybeReloadOnChunkError } from '@/utils';
import { useEffect } from 'react';

/**
 * ChunkErrorReloader
 *
 * Recovers from post-deployment chunk-load failures. When a new build ships, a
 * long-lived tab still references the previous build's hashed chunks, so the
 * next navigation throws a `ChunkLoadError`. This component performs a single
 * guarded reload so the tab silently moves onto the current build.
 *
 * Why global listeners (in addition to the route error boundaries):
 * - Failed dynamic `import()`s frequently surface as an unhandled promise
 *   rejection, which never reaches a React error boundary.
 *
 * Intentionally independent of Application Insights — recovery must work even
 * when telemetry is disabled. Renders no UI.
 */
export function ChunkErrorReloader() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      maybeReloadOnChunkError(event.error ?? event.message);
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      maybeReloadOnChunkError(event.reason);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return null;
}
